import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { EnvironmentInjector } from '@next/core-lib/environment';
import { DataSeries } from '@next/core-lib/models';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { FeedAdvice } from 'src/app/feed-advice/models/feed-advice.models';
import { ReferenceValueSheet } from 'src/app/reference-value-sheet/models/reference-value-sheet.model';

import { MasterDataSettings } from 'src/app/settings/models/setting.model';

import { Environment } from '../../../environment';
import { CarbonFootprintReadiness } from '../models/carbon-footprint-readiness.model';
import {
  CarbonFootprintResponse,
  Delivery,
  FarmResource,
  FarmResourcesDivision,
} from '../models/cycle.model';
import {
  Cycle,
  FeedAdviceUpdateValidResponse,
  GetCycleResponse,
  GetDeliveriesResponse,
  GetFarmResourcesResponse,
  GetProduct,
  GetProductsToConsumeResponse,
  GrowthDataSeriesResponse,
  ReferenceValueSheetUpdateValidResponse,
  SlaughterInvoicesResponse,
  SlaughterReportBarnLinksRequest,
  SlaughterReportsResponse,
  UpsertBalances,
  UpsertCycleResponse,
  UpsertFarmResourceResponse,
  WdoDataSeriesResponse,
} from '../models/flock-registration-service.model';

@Injectable({
  providedIn: 'root',
})
export class FlockRegistrationService {
  private readonly http = inject(HttpClient);
  private readonly env = inject<Environment>(EnvironmentInjector);

  private baseUrl = `${this.env.API_BASE_URL}/service/flock-registration/secure`;

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {}

  getCycles(): Observable<Cycle[] | HttpErrorResponse> {
    return this.http.get<GetCycleResponse>(`${this.baseUrl}/cycle`, {}).pipe(
      map((response) => response.cycles),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 404) {
          return of(<Cycle[]>[]);
        } else {
          return throwError({ message: error.message });
        }
      }),
    );
  }

  calculateBalanceDraft(cycleId: number): Observable<Cycle | HttpErrorResponse> {
    return this.http.get<Cycle>(`${this.baseUrl}/cycle/${cycleId}/balance/draft`, {}).pipe(
      map((cycle) => cycle),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 404) {
          return of(<Cycle>{});
        } else {
          return throwError({ message: error.message });
        }
      }),
    );
  }

  upsertCycle(cycle: Cycle): Observable<UpsertCycleResponse | HttpErrorResponse> {
    return this.http
      .post<UpsertCycleResponse>(
        `${this.baseUrl}/cycle`,
        { ...cycle, balancesPerDeliveryLine: null },
        {},
      )
      .pipe(
        map((d) => d),
        catchError((error: HttpErrorResponse) => {
          if (error.status === 404) {
            return of(<UpsertCycleResponse>{});
          } else {
            return throwError({ message: error.message });
          }
        }),
      );
  }

  upsertMasterData(masterData: MasterDataSettings) {
    return this.http.post(`${this.baseUrl}/masterdata`, masterData, {}).pipe(
      map((d) => d),
      catchError((error: HttpErrorResponse) => throwError(() => error)),
    );
  }

  upsertBalances(
    upsertBalances: UpsertBalances,
  ): Observable<UpsertCycleResponse | HttpErrorResponse> {
    return this.http.post<UpsertCycleResponse>(`${this.baseUrl}/balance`, upsertBalances, {}).pipe(
      map((d) => d),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 404) {
          return of(<UpsertCycleResponse>{});
        } else {
          return throwError({ message: error.message });
        }
      }),
    );
  }

  deleteCycle(id: number) {
    return this.http.delete(`${this.baseUrl}/cycle/${id}`);
  }

  public getProductsToConsume(
    cycleId: number,
    previousCycleCount: number,
  ): Observable<GetProduct[]> {
    return this.http
      .get<GetProductsToConsumeResponse>(
        `${this.baseUrl}/delivery/products-to-consume/cycle/${cycleId}/countofpreviouscyclesback/${previousCycleCount}`,
      )
      .pipe(map((d) => d.productsByDeliveries));
  }

  getDeliveries(): Observable<Delivery[] | HttpErrorResponse> {
    const params: { [p: string]: string } = {};
    params['deliveriesUsedInBalanceOnly'] = 'true';
    return this.http.get<GetDeliveriesResponse>(`${this.baseUrl}/delivery`, { params }).pipe(
      map((response) => response.deliveries),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 404) {
          return of(<Delivery[]>[]);
        } else {
          return throwError({ message: error.message });
        }
      }),
    );
  }

  public getCycleWdoDataSeriesByCycleIdAndBarnId(
    cycleId: number,
    barnId: number,
  ): Observable<DataSeries<number, number> | HttpErrorResponse> {
    return this.http
      .get<WdoDataSeriesResponse>(`${this.baseUrl}/cycle/${cycleId}/barn/${barnId}/wdo/dataseries`)
      .pipe(
        map((response) => response.series),
        catchError((error: HttpErrorResponse) => {
          if (error.status === 404) {
            return of(undefined);
          } else {
            return throwError({ message: error.message });
          }
        }),
      );
  }

  public getCycleGrowthPredictionDataSeriesByCycleIdAndBarnId(
    cycleId: number,
    barnId: number,
  ): Observable<DataSeries<number, number> | HttpErrorResponse> {
    return this.http
      .get<GrowthDataSeriesResponse>(
        `${this.baseUrl}/performanceprediction/${cycleId}/barn/${barnId}/growthprediction/dataseries`,
      )
      .pipe(
        map((response) => response.series),
        catchError((error: HttpErrorResponse) => {
          if (error.status === 404) {
            return of(undefined);
          } else {
            return throwError({ message: error.message });
          }
        }),
      );
  }

  public getSlaughterReports(
    fromDate: string,
  ): Observable<SlaughterReportsResponse | HttpErrorResponse> {
    return this.http
      .get<SlaughterReportsResponse>(`${this.baseUrl}/slaughterreport/?startDate=${fromDate}`)
      .pipe(
        catchError((error: HttpErrorResponse) => {
          if (error.status === 404) {
            return of({
              slaughterReports: [],
            });
          } else {
            return throwError({ message: error.message });
          }
        }),
      );
  }

  public importFromSlaughterReports(
    cycleId: number,
    slaughterReportBarnLinkRequest: SlaughterReportBarnLinksRequest,
  ): Observable<unknown | HttpErrorResponse> {
    return this.http
      .post(`${this.baseUrl}/cycle/${cycleId}/fromslaughterreport`, slaughterReportBarnLinkRequest)
      .pipe(
        map((d) => d),
        catchError((error: HttpErrorResponse) => throwError({ message: error.message })),
      );
  }

  public importManualDeliveriesCsv(cycleId: number, file: File): Observable<void> {
    const formData = new FormData();
    formData.append('file', file);

    return this.http
      .post<void>(`${this.baseUrl}/manualdelivery/import/csv`, formData, {
        params: { cycleId },
      })
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  public importDeparturesCsv(cycleId: number, file: File): Observable<void> {
    const formData = new FormData();
    formData.append('file', file);

    return this.http
      .post<void>(`${this.baseUrl}/cycle/${cycleId}/departures/import/csv`, formData)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  public getSlaughterInvoices(
    fromDate: string,
    linkedCycleId?: number,
  ): Observable<SlaughterInvoicesResponse> {
    const linkedCycleIdParamValue = linkedCycleId ?? '';
    return this.http.get<SlaughterInvoicesResponse>(
      `${this.baseUrl}/slaughterinvoice?fromDate=${fromDate}&linkedCycleId=${linkedCycleIdParamValue}`,
    );
  }

  public linkSlaughterInvoices(cycleId: number, ids: number[]) {
    return this.http.post(`${this.baseUrl}/slaughterinvoice`, { linkedCycleId: cycleId, ids });
  }

  checkUpdateValidityFeedAdvice(
    feedAdvice: FeedAdvice,
  ): Observable<FeedAdviceUpdateValidResponse | HttpErrorResponse> {
    return this.http
      .get<FeedAdviceUpdateValidResponse>(
        `${this.baseUrl}/cycle/fa/${feedAdvice.id}/check-update-validity`,
      )
      .pipe(
        map((response) => response),
        catchError((error: HttpErrorResponse) => {
          if (error.status === 404) {
            return of(undefined);
          } else {
            return throwError({ message: error.message });
          }
        }),
      );
  }

  checkUpdateValidityReferenceValueSheet(
    referenceValueSheet: ReferenceValueSheet,
  ): Observable<ReferenceValueSheetUpdateValidResponse | HttpErrorResponse> {
    return this.http
      .get<ReferenceValueSheetUpdateValidResponse>(
        `${this.baseUrl}/cycle/rvs/${referenceValueSheet.id}/check-update-validity`,
      )
      .pipe(
        map((response) => response),
        catchError((error: HttpErrorResponse) => {
          if (error.status === 404) {
            return of(undefined);
          } else {
            return throwError({ message: error.message });
          }
        }),
      );
  }

  getFarmResources(): Observable<
    { farmResources: FarmResource[]; cycleResources: FarmResourcesDivision[] } | HttpErrorResponse
  > {
    return this.http
      .get<GetFarmResourcesResponse>(`${this.baseUrl}/sustainability/farm-resources`)
      .pipe(
        map((response) => ({
          farmResources: response.farmResources,
          cycleResources: response.cycleResources,
        })),
        catchError((error: HttpErrorResponse) => {
          if (error.status === 404) {
            return of({ farmResources: [], cycleResources: [] });
          } else {
            return throwError(() => new Error(error.message));
          }
        }),
      );
  }

  upsertFarmResource(
    farmResource: FarmResource,
  ): Observable<UpsertFarmResourceResponse | HttpErrorResponse> {
    return this.http
      .post<UpsertFarmResourceResponse>(
        `${this.baseUrl}/sustainability/farm-resource`,
        farmResource,
        {},
      )
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => new Error(error.message))));
  }

  deleteFarmResource(id: number) {
    return this.http.delete(`${this.baseUrl}/sustainability/farm-resource/${id}`);
  }

  getCarbonFootprint(cycleId: number): Observable<CarbonFootprintResponse | HttpErrorResponse> {
    return this.http
      .get<CarbonFootprintResponse>(
        `${this.baseUrl}/sustainability/carbon-footprint?cycleId=${cycleId}`,
      )
      .pipe(
        map((response) => response),
        catchError((error: HttpErrorResponse) => {
          if (error.status === 404) {
            return of(<CarbonFootprintResponse>undefined);
          } else {
            return throwError(() => new Error(error.message));
          }
        }),
      );
  }

  getCarbonFootprintReadiness(cycleId: number): Observable<CarbonFootprintReadiness> {
    return this.http.get<CarbonFootprintReadiness>(
      `${this.baseUrl}/sustainability/carbon-footprint/readiness`,
      { params: { cycleId } },
    );
  }

  calculateCarbonFootprint(cycleId: number): Observable<void | HttpErrorResponse> {
    return this.http
      .post<void>(`${this.baseUrl}/sustainability/carbon-footprint?cycleId=${cycleId}`, null)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  refreshCarbonFootprint(cycleId: number): Observable<void | HttpErrorResponse> {
    return this.http
      .post<void>(
        `${this.baseUrl}/sustainability/refresh-carbon-footprint?cycleId=${cycleId}`,
        null,
      )
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }
}
